/**
 * QRScanner Component
 * WhatsApp QR code connection flow
 * QR codes auto-refresh via socket - no manual refresh needed during scanning
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Smartphone,
  RefreshCw,
  Check,
  AlertCircle,
  Wifi,
  WifiOff,
  LogOut,
  X
} from 'lucide-react';
import QRCode from 'qrcode';
import { useWhatsApp } from '../../context/WhatsAppContext';
import './QRScanner.css';

// Poll interval for checking connection status
const STATUS_POLL_INTERVAL = 5000; // 5 seconds

function QRScanner() {
  const {
    connectionStatus,
    phoneNumber,
    displayName,
    qrCode,
    generateQRCode,
    disconnect,
    checkConnectionStatus,
    isSocketConnected
  } = useWhatsApp();

  const [qrImageUrl, setQrImageUrl] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanningTime, setScanningTime] = useState(0);
  const pollingRef = useRef(null);
  const timerRef = useRef(null);

  // Generate QR image when code changes (auto-updates via socket)
  useEffect(() => {
    if (qrCode) {
      QRCode.toDataURL(qrCode, {
        width: 280,
        margin: 2,
        color: {
          dark: '#111b21',
          light: '#ffffff'
        }
      }).then(url => {
        setQrImageUrl(url);
        console.log('[QRScanner] QR code updated');
      }).catch(err => {
        console.error('Failed to generate QR image:', err);
        setError('Failed to generate QR code');
      });
    }
  }, [qrCode]);

  // Poll for connection status while showing QR
  useEffect(() => {
    if (connectionStatus === 'QR_READY' || connectionStatus === 'INITIALIZING') {
      // Start polling for status
      pollingRef.current = setInterval(() => {
        checkConnectionStatus();
      }, STATUS_POLL_INTERVAL);

      // Track scanning time
      timerRef.current = setInterval(() => {
        setScanningTime(prev => prev + 1);
      }, 1000);

      return () => {
        if (pollingRef.current) clearInterval(pollingRef.current);
        if (timerRef.current) clearInterval(timerRef.current);
      };
    } else {
      // Clear polling when not in QR state
      if (pollingRef.current) clearInterval(pollingRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
      setScanningTime(0);
    }
  }, [connectionStatus, checkConnectionStatus]);

  // Handle connect - starts the QR flow
  const handleConnect = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setScanningTime(0);
      await generateQRCode();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [generateQRCode]);

  // Handle cancel - stops the QR flow and destroys session
  const handleCancel = useCallback(async () => {
    try {
      setLoading(true);
      await disconnect();
      setQrImageUrl(null);
      setScanningTime(0);
    } catch (err) {
      console.error('Error cancelling:', err);
    } finally {
      setLoading(false);
    }
  }, [disconnect]);

  // Handle disconnect
  const handleDisconnect = useCallback(async () => {
    try {
      setLoading(true);
      await disconnect();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [disconnect]);

  // Format scanning time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  // Connected state
  if (connectionStatus === 'CONNECTED') {
    return (
      <div className="wa-qr-container connected">
        <div className="wa-connected-card">
          <div className="wa-connected-icon">
            <Check size={48} />
          </div>
          <h2>WhatsApp Connected</h2>
          <div className="wa-connected-info">
            <div className="wa-phone-number">
              <Smartphone size={20} />
              <span>{phoneNumber}</span>
            </div>
            {displayName && (
              <p className="wa-display-name">{displayName}</p>
            )}
          </div>
          <div className="wa-status-indicator online">
            <Wifi size={16} />
            <span>Online</span>
          </div>
          <button
            className="wa-disconnect-btn"
            onClick={handleDisconnect}
            disabled={loading}
          >
            <LogOut size={18} />
            Disconnect
          </button>
        </div>
      </div>
    );
  }

  // QR Ready state - show QR and wait for scan
  if ((connectionStatus === 'QR_READY' || connectionStatus === 'INITIALIZING') && qrImageUrl) {
    return (
      <div className="wa-qr-container">
        <div className="wa-qr-card">
          <h2>Scan QR Code</h2>
          <p className="wa-qr-instructions">
            Point your phone camera at this QR code to connect
          </p>

          <div className="wa-qr-wrapper">
            <img src={qrImageUrl} alt="WhatsApp QR Code" className="wa-qr-image" />
            <div className="wa-qr-scanning-indicator">
              <RefreshCw size={16} className="spinning" />
              <span>Waiting for scan...</span>
            </div>
          </div>

          <div className="wa-scanning-status">
            <span>Scanning for {formatTime(scanningTime)}</span>
            {scanningTime > 60 && (
              <p className="wa-scanning-hint">
                Make sure your phone camera is pointed at the QR code
              </p>
            )}
          </div>

          <div className="wa-qr-steps">
            <h4>How to connect:</h4>
            <ol>
              <li>Open WhatsApp on your phone</li>
              <li>Tap <strong>Menu</strong> or <strong>Settings</strong></li>
              <li>Select <strong>Linked Devices</strong></li>
              <li>Tap <strong>Link a Device</strong></li>
              <li>Point your phone at this screen</li>
            </ol>
          </div>

          <div className="wa-qr-actions">
            <button
              className="wa-cancel-btn"
              onClick={handleCancel}
              disabled={loading}
            >
              <X size={16} />
              Cancel
            </button>
          </div>

          <div className="wa-socket-status">
            {isSocketConnected ? (
              <span className="online"><Wifi size={14} /> Real-time updates active</span>
            ) : (
              <span className="offline"><WifiOff size={14} /> Connecting...</span>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Connecting state
  if (connectionStatus === 'CONNECTING' || loading) {
    return (
      <div className="wa-qr-container">
        <div className="wa-qr-card loading">
          <div className="wa-loading-spinner">
            <RefreshCw size={48} className="spinning" />
          </div>
          <h2>Connecting to WhatsApp...</h2>
          <p>This may take a few moments</p>
        </div>
      </div>
    );
  }

  // Disconnected state (initial)
  return (
    <div className="wa-qr-container">
      <div className="wa-qr-card initial">
        <div className="wa-whatsapp-logo">
          <svg viewBox="0 0 39 39" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M33.25 5.75C29.5 2 24.5 0 19.5 0C8.5 0 0 8.5 0 19.5C0 23 1 26.5 2.5 29.5L0 39L10 36.5C13 38 16 38.5 19.5 38.5C30 38.5 39 30 39 19C39 14 37 9.5 33.25 5.75ZM19.5 35.5C16.5 35.5 13.5 34.5 11 33L10.5 32.5L4.5 34L6 28.5L5.5 27.5C3.5 25 2.5 22 2.5 19.5C2.5 10 10.5 3 19.5 3C23.5 3 27.5 4.5 30.5 7.5C33.5 10.5 35 14.5 35 18.5C35.5 28 28.5 35.5 19.5 35.5ZM28.5 23.5C28 23 24 21 23.5 21C23 20.5 22.5 20.5 22 21C21.5 21.5 20.5 22.5 20 23C19.5 23.5 19 23.5 18.5 23C18 22.5 16 21.5 14.5 20C13 18.5 12 17 11.5 16.5C11 16 11 15.5 11.5 15C11.5 14.5 12.5 13.5 13 13C13.5 12.5 13 12 13 11.5C13 11 11.5 7.5 11 6.5C10.5 5.5 10 5.5 9.5 5.5H8.5C8 5.5 7 6 6.5 6.5C5.5 7.5 4.5 9 4.5 11C4.5 13 5 15 6.5 17.5C9.5 22.5 14 27 19.5 29.5C21 30 22 30.5 23 30.5C24 31 26 30.5 27 30C28 29 29 27.5 29 26.5C29 25.5 29 25 29 24.5L28.5 23.5Z"
              fill="#25D366"
            />
          </svg>
        </div>

        <h2>WhatsApp Business</h2>
        <p>Connect your WhatsApp to start messaging customers</p>

        {error && (
          <div className="wa-error-message">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        <button
          className="wa-connect-btn"
          onClick={handleConnect}
          disabled={loading}
        >
          {loading ? (
            <>
              <RefreshCw size={18} className="spinning" />
              Connecting...
            </>
          ) : (
            <>
              <Smartphone size={18} />
              Connect WhatsApp
            </>
          )}
        </button>

        <div className="wa-note">
          <p>
            Your personal messages will remain on your phone. Only business conversations
            will be synced to this dashboard.
          </p>
        </div>
      </div>
    </div>
  );
}

export default QRScanner;
